import React from 'react'
import type { ContentfulImageFormat } from '~/utils/assets/contentfulImage'
import { getContentTypeFromImageFormat } from '~/utils/assets/contentfulImage'

export interface ImageSize {
  width: number
  height: number
  media: string
  densities: number[]
}

interface ContentfulImageProps extends Omit<React.HTMLAttributes<HTMLPictureElement>, 'children'> {
  src: string
  alt: string
  sizes: ImageSize[]
  quality?: number
  format?: ContentfulImageFormat
  contentType?: string
  imageClassName?: string
}

export const isContentfulUrl = (url?: string): boolean => {
  return !!url?.includes('images.ctfassets.net')
}

type FormatOptions = {
  width?: number
  height?: number
  format?: ContentfulImageFormat
  quality?: number
}

function normalizeImageUrl(url: string | undefined): string {
  if (!url) {
    return ''
  }
  if (url.startsWith('//')) {
    return `https:${url}`
  }
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) {
    return url
  }
  return `https://${url}`
}

function formatContentfulSrc(url: string | undefined, options: FormatOptions = {}): string {
  if (!url) {
    return ''
  }

  const normalizedUrl = normalizeImageUrl(url)

  if (!options) {
    return normalizedUrl
  }

  const { width = 0, height = 0, format = 'jpg', quality = 85 } = options

  const fm = format === 'jpg' ? `fm=jpg&q=${quality}&fl=progressive` : `fm=${format}`
  return `${normalizedUrl}?${fm}&fit=fill&w=${width}&h=${height}`
}

function generateSrcSet(
  url: string,
  width: number,
  height: number,
  densities: number[],
  quality: number,
  format: ContentfulImageFormat,
  shouldFormatSrc: boolean
): string {
  return densities
    .map((dpi) => {
      const formattedSrc = shouldFormatSrc
        ? formatContentfulSrc(url, {
            width: width * dpi,
            height: height * dpi,
            format,
            quality,
          })
        : normalizeImageUrl(url)

      return `${formattedSrc} ${dpi}x`
    })
    .join(', ')
}

export function ContentfulImage({
  src,
  alt,
  sizes,
  quality = 85,
  format = 'jpg',
  contentType,
  imageClassName,
  ...pictureProps
}: Readonly<ContentfulImageProps>) {
  const smallestSize = sizes.at(-1)
  const shouldFormatSrc = isContentfulUrl(src)
  const sourceType = shouldFormatSrc
    ? getContentTypeFromImageFormat(format)
    : contentType ?? getContentTypeFromImageFormat(format)
  const smallestImageOptions = smallestSize
    ? {
        width: smallestSize.width,
        height: smallestSize.height,
        format,
        quality,
      }
    : undefined
  const imgSrc = shouldFormatSrc
    ? formatContentfulSrc(src, smallestImageOptions)
    : normalizeImageUrl(src)
  const imageStyle: React.CSSProperties = {
    display: 'block',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    width: '100%',
  }

  return (
    <picture {...pictureProps}>
      {src
        ? sizes.map(({ width, height, media, densities }) => (
            <source
              key={media}
              media={media}
              type={sourceType}
              srcSet={generateSrcSet(
                src,
                width,
                height,
                densities,
                quality,
                format,
                shouldFormatSrc
              )}
            />
          ))
        : null}
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={imgSrc} alt={alt} className={imageClassName} style={imageStyle} />
      ) : null}
    </picture>
  )
}
